import React, { useState, useEffect, useRef } from 'react';
import { IonPage, IonContent, IonModal, IonRefresher, IonRefresherContent } from '@ionic/react';
import { usePantryStore, PantryItem, ReceiptItem } from '../../store/pantryStore';
import { wsClient } from '../../services/websocket';
import BackHeader from '../../components/BackHeader';
import FeaturePageHeader from '../../components/FeaturePageHeader';
import FeatureMobileHeader from '../../components/FeatureMobileHeader';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import Icon from '../../components/Icon';
import { showToast } from '../../components/Toast';
import ConfirmDialog from '../../components/ConfirmDialog';
import heic2any from 'heic2any';

const getDaysUntilExpiry = (item: PantryItem): number | null => {
  if (!item.expiry_date) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const exp = new Date(item.expiry_date);
  exp.setHours(0, 0, 0, 0);
  return Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

const getExpiryStatus = (item: PantryItem): 'expired' | 'today' | 'soon' | 'ok' => {
  const days = getDaysUntilExpiry(item);
  if (days === null) return 'ok';
  if (days < 0) return 'expired';
  if (days === 0) return 'today';
  if (days <= 3) return 'soon';
  return 'ok';
};

const formatExpiryDate = (item: PantryItem): string => {
  if (!item.expiry_date) return 'No expiry';
  const date = new Date(item.expiry_date);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const CATEGORY_ICONS: Record<string, string> = {
  'Fruits & Veg': 'eco',
  'Dairy & Eggs': 'egg_alt',
  'Meat & Fish': 'restaurant',
  'Tins & Packets': 'inventory',
  'Bakery': 'bakery_dining',
  'Frozen': 'ac_unit',
  'World Foods': 'public',
  'Grains & Rice': 'grain',
  'Pasta & Noodles': 'ramen_dining',
  'Drinks': 'local_cafe',
  'Spices & Herbs': 'spa',
  'Sauces & Pastes': 'water_drop',
  'Condiments': 'inventory',
  'Legumes & Pulses': 'grain',
  'Nuts & Seeds': 'eco',
  'Snacks': 'cookie',
  'Baking': 'cake',
  'Baby & Toddler': 'child_care',
  'Breakfast & Cereal': 'free_breakfast',
  'Oils & Vinegars': 'water_drop',
  'Ready Meals': 'lunch_dining',
  'Deli & Chilled': 'kitchen',
  'Other': 'inventory_2',
};

// Common UK pantry items by category for quick-add suggestions
const SUGGESTIONS: Record<string, Array<{ name: string; icon: string }>> = {
  'Fruits & Veg': [
    { name: 'Bananas', icon: 'nutrition' }, { name: 'Apples', icon: 'nutrition' },
    { name: 'Potatoes', icon: 'eco' }, { name: 'Onions', icon: 'eco' },
    { name: 'Carrots', icon: 'eco' }, { name: 'Tomatoes', icon: 'nutrition' },
    { name: 'Peppers', icon: 'eco' }, { name: 'Avocados', icon: 'eco' },
    { name: 'Spinach', icon: 'spa' }, { name: 'Mushrooms', icon: 'eco' },
    { name: 'Lettuce', icon: 'spa' }, { name: 'Cucumber', icon: 'eco' },
    { name: 'Broccoli', icon: 'eco' }, { name: 'Garlic', icon: 'eco' },
    { name: 'Lemons', icon: 'nutrition' }, { name: 'Ginger', icon: 'eco' },
  ],
  'Dairy & Eggs': [
    { name: 'Whole Milk', icon: 'water_drop' }, { name: 'Semi-Skimmed Milk', icon: 'water_drop' },
    { name: 'Butter', icon: 'egg_alt' }, { name: 'Cheddar', icon: 'egg_alt' },
    { name: 'Eggs (free range)', icon: 'egg' }, { name: 'Greek Yogurt', icon: 'icecream' },
    { name: 'Double Cream', icon: 'water_drop' }, { name: 'Oat Milk', icon: 'water_drop' },
    { name: 'Mozzarella', icon: 'egg_alt' }, { name: 'Parmesan', icon: 'egg_alt' },
  ],
  'Meat & Fish': [
    { name: 'Chicken Breast', icon: 'restaurant' }, { name: 'Minced Beef', icon: 'restaurant' },
    { name: 'Salmon Fillets', icon: 'set_meal' }, { name: 'Bacon', icon: 'restaurant' },
    { name: 'Sausages', icon: 'restaurant' }, { name: 'Cod Fillets', icon: 'set_meal' },
    { name: 'Lamb Mince', icon: 'restaurant' }, { name: 'Prawns', icon: 'set_meal' },
  ],
  'Tins & Packets': [
    { name: 'Basmati Rice', icon: 'grain' }, { name: 'Pasta', icon: 'ramen_dining' },
    { name: 'Chopped Tomatoes', icon: 'inventory' }, { name: 'Baked Beans', icon: 'inventory' },
    { name: 'Chickpeas', icon: 'inventory' }, { name: 'Coconut Milk', icon: 'inventory' },
    { name: 'Tuna', icon: 'inventory' }, { name: 'Sweetcorn', icon: 'inventory' },
    { name: 'Lentils', icon: 'grain' }, { name: 'Noodles', icon: 'ramen_dining' },
    { name: 'Flour (plain)', icon: 'grain' }, { name: 'Sugar', icon: 'grain' },
    { name: 'Olive Oil', icon: 'water_drop' }, { name: 'Soy Sauce', icon: 'inventory' },
  ],
  'Bakery': [
    { name: 'Bread (white)', icon: 'bakery_dining' }, { name: 'Bread (wholemeal)', icon: 'bakery_dining' },
    { name: 'Wraps', icon: 'bakery_dining' }, { name: 'Crumpets', icon: 'bakery_dining' },
    { name: 'Rolls', icon: 'bakery_dining' }, { name: 'Pitta Bread', icon: 'bakery_dining' },
  ],
  'Frozen': [
    { name: 'Frozen Peas', icon: 'ac_unit' }, { name: 'Fish Fingers', icon: 'ac_unit' },
    { name: 'Frozen Chips', icon: 'ac_unit' }, { name: 'Ice Cream', icon: 'icecream' },
    { name: 'Pizza', icon: 'local_pizza' }, { name: 'Frozen Veg Mix', icon: 'ac_unit' },
  ],
  'World Foods': [
    { name: 'Naan Bread', icon: 'bakery_dining' }, { name: 'Tortillas', icon: 'bakery_dining' },
    { name: 'Curry Paste', icon: 'inventory' }, { name: 'Sriracha', icon: 'inventory' },
    { name: 'Miso Paste', icon: 'inventory' }, { name: 'Rice Noodles', icon: 'ramen_dining' },
    { name: 'Jasmine Rice', icon: 'grain' }, { name: 'Sushi Rice', icon: 'grain' },
    { name: 'Gochujang', icon: 'inventory' }, { name: 'Fish Sauce', icon: 'water_drop' },
    { name: 'Hoisin Sauce', icon: 'inventory' }, { name: 'Tahini', icon: 'inventory' },
    { name: 'Harissa Paste', icon: 'inventory' }, { name: 'Plantain', icon: 'nutrition' },
    { name: 'Scotch Bonnet', icon: 'eco' }, { name: 'Jerk Seasoning', icon: 'inventory' },
  ],
};

const ALL_CATEGORIES = [
  'All', 'Fruits & Veg', 'Dairy & Eggs', 'Meat & Fish', 'Grains & Rice',
  'Pasta & Noodles', 'Bakery', 'Frozen', 'Tins & Packets', 'Drinks',
  'Spices & Herbs', 'Sauces & Pastes', 'Condiments', 'Legumes & Pulses',
  'Nuts & Seeds', 'Snacks', 'Baking', 'World Foods', 'Baby & Toddler',
  'Breakfast & Cereal', 'Oils & Vinegars', 'Ready Meals', 'Deli & Chilled', 'Other',
];

const PantryPage: React.FC = () => {
  const isDesktopV2 = useIsDesktop();
  const {
    items, expiringItems, isLoading, isScanning, receiptItems,
    fetchItems, fetchExpiring, addItem: storeAddItem,
    updateItem, deleteItem: storeDeleteItem, handleWsMessage,
    scanReceipt, addBatchItems, clearReceiptItems,
  } = usePantryStore();

  const [activeCategory, setActiveCategory] = useState('All');
  const [activeLocation, setActiveLocation] = useState<'all' | 'pantry' | 'fridge' | 'freezer' | 'other'>('all');
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newExpiry, setNewExpiry] = useState('');
  const [newCategory, setNewCategory] = useState('Fruits & Veg');
  const [addMode, setAddMode] = useState<'suggestions' | 'manual' | 'receipt'>('suggestions');
  const [suggestionCategory, setSuggestionCategory] = useState('Fruits & Veg');
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; open: boolean }>({ id: '', open: false });
  const [localReceiptItems, setLocalReceiptItems] = useState<ReceiptItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit modal state
  const [editItem, setEditItem] = useState<PantryItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editQuantity, setEditQuantity] = useState(1);
  const [editUnit, setEditUnit] = useState('');
  const [editExpiry, setEditExpiry] = useState('');

  const openEditModal = (item: PantryItem) => {
    setEditItem(item);
    setEditName(item.name);
    setEditCategory(item.category);
    setEditQuantity(item.quantity);
    setEditUnit(item.unit);
    setEditExpiry(item.expiry_date || '');
  };

  const closeEditModal = () => {
    setEditItem(null);
  };

  const handleSaveEdit = async () => {
    if (!editItem || !editName.trim()) return;
    try {
      await updateItem(editItem.id, {
        name: editName.trim(),
        category: editCategory,
        quantity: editQuantity,
        unit: editUnit,
        expiry_date: editExpiry || undefined,
      });
      showToast('Item updated', 'success');
      closeEditModal();
    } catch (err) {
      console.error('Failed to update pantry item', err);
      showToast('Failed to update item', 'error');
    }
  };

  const handleDeleteFromEdit = async () => {
    if (!editItem) return;
    try {
      await storeDeleteItem(editItem.id);
      showToast('Item deleted', 'success');
      closeEditModal();
    } catch (err) {
      console.error('Failed to delete pantry item', err);
      showToast('Failed to delete item', 'error');
    }
  };

  // Fetch items and expiring items on mount
  useEffect(() => {
    fetchItems();
    fetchExpiring();
  }, []);

  // WebSocket subscriptions
  useEffect(() => {
    const unsubs = [
      wsClient.on('PANTRY_ITEM_ADDED', handleWsMessage),
      wsClient.on('PANTRY_ITEM_UPDATED', handleWsMessage),
      wsClient.on('PANTRY_ITEM_DELETED', handleWsMessage),
    ];
    return () => unsubs.forEach(u => u());
  }, [handleWsMessage]);

  // Category filter via store
  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    if (cat === 'All') {
      fetchItems();
    } else {
      fetchItems({ category: cat });
    }
  };

  const handleUpdateQty = async (item: PantryItem, delta: number) => {
    try {
      const newQty = Number(item.quantity) + delta;
      if (newQty <= 0) {
        await storeDeleteItem(item.id);
      } else {
        await updateItem(item.id, { quantity: newQty });
      }
    } catch {
      showToast('Failed to update quantity', 'error');
    }
  };

  const handleDeleteItem = async (id: string) => {
    try {
      await storeDeleteItem(id);
      showToast('Item deleted', 'success');
    } catch (err) {
      console.error('Failed to delete pantry item', err);
      showToast('Failed to delete item', 'error');
    }
  };

  const handleAddItem = async () => {
    if (!newName.trim()) return;
    try {
      await storeAddItem({
        name: newName.trim(),
        category: newCategory,
        quantity: 1,
        unit: 'pcs',
        expiry_date: newExpiry || undefined,
        location: 'pantry',
      });
      showToast(`${newName.trim()} added to pantry`, 'success');
      setNewName('');
      setNewExpiry('');
      setShowAdd(false);
    } catch (err) {
      console.error('Failed to add pantry item', err);
      showToast('Failed to add item', 'error');
    }
  };

  const handleAddSuggestion = async (suggestion: { name: string; icon: string }, category: string) => {
    try {
      const exists = items.find(it => it.name.toLowerCase() === suggestion.name.toLowerCase());
      if (exists) {
        await updateItem(exists.id, { quantity: Number(exists.quantity) + 1 });
      } else {
        await storeAddItem({
          name: suggestion.name,
          category,
          quantity: 1,
          unit: 'pcs',
          location: 'pantry',
        });
      }
    } catch {
      showToast('Failed to add item', 'error');
    }
  };

  const handleRefresh = async (e: CustomEvent) => {
    await Promise.all([fetchItems(activeCategory === 'All' ? undefined : { category: activeCategory }), fetchExpiring()]);
    e.detail.complete();
  };

  // Sync receipt items from store to local state for toggling
  useEffect(() => {
    setLocalReceiptItems(receiptItems);
  }, [receiptItems]);

  const handleReceiptFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-selected
    e.target.value = '';

    try {
      let processedFile = file;
      let mimeType = file.type || 'image/jpeg';

      // Convert HEIC/HEIF to JPEG for browser & API compatibility
      const isHeic = /\.(heic|heif)$/i.test(file.name) ||
        ['image/heic', 'image/heif'].includes(mimeType.toLowerCase());

      if (isHeic) {
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
        processedFile = Array.isArray(converted) ? converted[0] as File : converted as File;
        mimeType = 'image/jpeg';
      }

      // Convert to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Strip data URL prefix to get raw base64
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(processedFile);
      });

      await scanReceipt(base64, mimeType);
      // Check if scan returned empty results
      const scannedItems = usePantryStore.getState().receiptItems;
      if (scannedItems.length === 0) {
        showToast('No items found. Try a clearer photo or add items manually.', 'error');
      }
    } catch (err: any) {
      console.error('Receipt scan failed:', err);
      showToast(err.message || 'Failed to scan receipt. Try a clearer photo.', 'error');
    }
  };

  const toggleReceiptItem = (index: number) => {
    setLocalReceiptItems(prev =>
      prev.map((item, i) => i === index ? { ...item, selected: !item.selected } : item)
    );
  };

  const handleAddReceiptItems = async () => {
    const selected = localReceiptItems.filter(item => item.selected);
    if (selected.length === 0) {
      showToast('No items selected', 'error');
      return;
    }
    try {
      await addBatchItems(selected);
      showToast(`${selected.length} item${selected.length !== 1 ? 's' : ''} added to pantry`, 'success');
      setLocalReceiptItems([]);
      setShowAdd(false);
      setAddMode('suggestions');
    } catch (err) {
      console.error('Failed to add receipt items', err);
      showToast('Failed to add items', 'error');
    }
  };

  const getItemIcon = (item: PantryItem): string => {
    return CATEGORY_ICONS[item.category] || 'inventory_2';
  };

  // Use items from store directly (already filtered by category when fetched)
  const filtered = items;

  const grouped = ALL_CATEGORIES.slice(1).reduce<Record<string, PantryItem[]>>((acc, cat) => {
    const catItems = filtered.filter(it => it.category === cat);
    if (catItems.length) acc[cat] = catItems;
    return acc;
  }, {});

  const expiringCount = expiringItems.length;

  const inputStyle: React.CSSProperties = {
    background: 'var(--fh-dark-bg)',
    color: 'var(--fh-dark-text)',
    border: '1px solid var(--fh-dark-border)',
    fontFamily: 'Manrope, sans-serif',
    borderRadius: '0.75rem',
    padding: '0.75rem 1rem',
    width: '100%',
    fontSize: '0.875rem',
    outline: 'none',
  };

  // ─── Desktop v2 ────────────────────────────────────────────────────────
  if (isDesktopV2) {
    // Fixed 3-zone layout: Fridge / Cupboards / Freezer
    const zoneFor = (it: PantryItem): 'Fridge' | 'Cupboards' | 'Freezer' => {
      const loc = (it.location || '').toLowerCase();
      if (loc.includes('freezer')) return 'Freezer';
      if (loc.includes('fridge')) return 'Fridge';
      return 'Cupboards';
    };
    const zones = {
      Fridge: items.filter(it => zoneFor(it) === 'Fridge'),
      Cupboards: items.filter(it => zoneFor(it) === 'Cupboards'),
      Freezer: items.filter(it => zoneFor(it) === 'Freezer'),
    };
    const lowCount = items.filter(it => (it.quantity || 0) <= 1 && (it.quantity || 0) > 0).length;
    const emptyCount = items.filter(it => (it.quantity || 0) === 0).length;

    // Auto-shopping: empty or very-low items → add to list.
    const autoShop = items.filter(it => (it.quantity || 0) === 0 || ((it.quantity || 0) <= 1 && it.quantity !== undefined));

    // Cook before it turns: items expiring in next 3 days, not yet empty.
    const cookSoon = items
      .filter(it => (it.quantity || 0) > 0 && it.expiry_date)
      .map(it => ({ it, days: getDaysUntilExpiry(it) }))
      .filter(r => r.days !== null && r.days! >= 0 && r.days! <= 3)
      .sort((a, b) => (a.days || 0) - (b.days || 0))
      .slice(0, 4);

    const categoryEmoji = (cat: string): string => {
      const c = (cat || '').toLowerCase();
      if (c.includes('dairy') || c.includes('milk') || c.includes('cheese')) return '🥛';
      if (c.includes('meat') || c.includes('chicken')) return '🍗';
      if (c.includes('fish')) return '🐟';
      if (c.includes('veg') || c.includes('produce')) return '🥦';
      if (c.includes('fruit')) return '🍎';
      if (c.includes('bread') || c.includes('bakery')) return '🍞';
      if (c.includes('egg')) return '🥚';
      if (c.includes('rice') || c.includes('grain')) return '🌾';
      if (c.includes('spice') || c.includes('herb')) return '🌿';
      if (c.includes('oil')) return '🫒';
      return '🥫';
    };

    return (
      <IonPage>
        <IonContent>
          <div className="fhv2-desktop">
            <div className="fhv2-today-head">
              <div>
                <h1 className="fhv2-today-title">{items.length} <em>items tracked</em></h1>
                <div className="fhv2-today-sub">{emptyCount} empty · {lowCount} running low · {expiringCount} expiring this week.</div>
              </div>
              <div className="fhv2-today-head-actions">
                <button className="fhv2-btn" onClick={() => { setAddMode('receipt'); setShowAdd(true); }}>Scan receipt</button>
                <button className="fhv2-btn is-primary" onClick={() => setShowAdd(true)}>+ Add item</button>
              </div>
            </div>

            <div className="fhv2-pantry-desktop-grid">
              <div>
                <div className="fhv2-pantry-zones-grid">
                  {(['Fridge', 'Cupboards', 'Freezer'] as const).map(zoneName => {
                    const list = zones[zoneName];
                    const expiring = list.filter(it => {
                      const d = getDaysUntilExpiry(it);
                      return d !== null && d >= 0 && d <= 7;
                    }).length;
                    const zoneToLocation: Record<typeof zoneName, 'fridge' | 'pantry' | 'freezer'> = {
                      Fridge: 'fridge', Cupboards: 'pantry', Freezer: 'freezer',
                    } as const;
                    const filterKey = zoneToLocation[zoneName];
                    return (
                      <div className="fhv2-zone" key={zoneName}>
                        <div
                          className="fhv2-zone-head"
                          onClick={() => setActiveLocation(filterKey)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setActiveLocation(filterKey); }}
                          style={{ cursor: 'pointer' }}
                          aria-label={`Filter to ${zoneName}`}
                        >
                          <h4>{zoneName}</h4>
                          {/* pantry-desktop.pdf shows temp/storage context alongside count. */}
                          <div className="fhv2-sub">
                            {list.length} items · {zoneName === 'Fridge' ? '3.2°C' : zoneName === 'Freezer' ? '−18°C' : 'dry store'}
                            {expiring > 0 ? ` · ${expiring} expiring` : ''}
                          </div>
                        </div>
                        {list.slice(0, 8).map(it => {
                          const days = getDaysUntilExpiry(it);
                          const low = (it.quantity || 0) <= 1 && (it.quantity || 0) > 0;
                          const empty = (it.quantity || 0) === 0;
                          const expiringSoon = days !== null && days <= 3;
                          return (
                            <div
                              key={it.id}
                              onClick={() => openEditModal(it)}
                              style={{ cursor: 'pointer' }}
                              className={`fhv2-pantry-item ${low ? 'is-low' : ''} ${empty ? 'is-empty' : ''} ${expiringSoon ? 'is-expiring' : ''}`}
                            >
                              <span className="fhv2-ic-pill">{categoryEmoji(it.category)}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>{it.name}</div>
                                {days !== null && (
                                  <div style={{ fontSize: 11, color: expiringSoon ? 'var(--fhv2-terra)' : 'var(--fhv2-muted)', marginTop: 2 }}>
                                    {days < 0 ? 'Expired' : days === 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'}`}
                                  </div>
                                )}
                              </div>
                              <span className="fhv2-q">{empty ? 'Empty' : low ? 'Low' : `×${it.quantity}`}</span>
                            </div>
                          );
                        })}
                        {list.length === 0 && <div style={{ fontSize: 12, color: 'var(--fhv2-dim)', padding: '6px 0' }}>Empty — add items.</div>}
                      </div>
                    );
                  })}
                </div>

                {autoShop.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <div className="fhv2-today-col-head">
                      <h4>Auto-shopping</h4>
                      <span className="fhv2-rail-more">{autoShop.length} items below minimum</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10, marginTop: 10 }}>
                      {autoShop.slice(0, 8).map(it => (
                        <div key={it.id} className="fhv2-pantry-item" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <span className="fhv2-ic-pill">{categoryEmoji(it.category)}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>{it.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--fhv2-muted)' }}>{(it.quantity || 0) === 0 ? 'Out of stock' : 'Low'}</div>
                          </div>
                          <span className="fhv2-q" style={{ color: 'var(--fhv2-terra)' }}>+ Add</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right rail */}
              <div className="fhv2-rail">
                {cookSoon.length > 0 && (
                  <div className="fhv2-rail-card">
                    <h4>Cook before it turns</h4>
                    {cookSoon.map(({ it, days }) => (
                      <div key={it.id} className="fhv2-stat-row">
                        <span className="fhv2-k">{categoryEmoji(it.category)} {it.name}</span>
                        <span className="fhv2-v" style={{ color: days === 0 ? 'var(--fhv2-terra)' : 'var(--fhv2-muted)' }}>
                          {days === 0 ? 'tonight' : `${days}d`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {emptyCount + lowCount > 0 && (
                  <div className="fhv2-rail-card">
                    <h4>Waste watched <span className="fhv2-rail-more">YTD</span></h4>
                    <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 36, color: '#fff', margin: '4px 0' }}>
                      {cookSoon.length} items
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--fhv2-muted)' }}>
                      Nudge-worthy expiries this week.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {items.length === 0 && (
              <div className="fhv2-empty" style={{ margin: '40px 40px 80px' }}>
                <Icon name="inventory_2" size={48} color="var(--fhv2-muted)" />
                <h4>Pantry is empty</h4>
                <p>Add items to start tracking expiries and stock.</p>
              </div>
            )}
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>
        <div className="fhv2-mpage fh-pb-safe" style={{ fontFamily: 'var(--fhv2-sans)' }}>
          <FeatureMobileHeader title="Pantry" rightIcon="add" onRight={() => setShowAdd(true)} />
          <div className="fhv2-mlead2">
            {items.length} items <em>catalogued.</em>
            <span className="sub">{expiringCount > 0 ? `${expiringCount} expiring soon` : 'Zero-waste nudges built in.'}</span>
          </div>

          {/* Zone breakdown (prototype shows Fridge/Freezer/Cupboard cards with counts) */}
          {items.length > 0 && (() => {
            const zones: Array<{ name: string; emoji: string; count: number; total: number; loc: string[] }> = [
              { name: 'Fridge', emoji: '🧊', count: 0, total: 0, loc: ['fridge'] },
              { name: 'Cupboard', emoji: '🥫', count: 0, total: 0, loc: ['pantry', 'cupboard', 'other'] },
              { name: 'Freezer', emoji: '❄️', count: 0, total: 0, loc: ['freezer'] },
            ];
            items.forEach(it => {
              const loc = (it.location || 'pantry').toLowerCase();
              const z = zones.find(z => z.loc.includes(loc));
              if (z) { z.total += 1; if ((it.quantity || 0) > 0) z.count += 1; }
            });
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                {zones.map(z => (
                  <div key={z.name} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--fhv2-line)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, marginBottom: 4 }}>{z.emoji}</div>
                    <div style={{ fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic', fontSize: 22, lineHeight: 1, color: '#fff' }}>{z.total}</div>
                    <div style={{ fontSize: 10, color: 'var(--fhv2-muted)', fontFamily: 'var(--fhv2-mono)', letterSpacing: '1px', marginTop: 4, textTransform: 'uppercase' }}>{z.name}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* USE TONIGHT — items expiring today/tomorrow with terra highlight */}
          {expiringItems.length > 0 && (
            <>
              <div className="fhv2-mlabel" style={{ color: 'var(--fhv2-terra)' }}>⚠️ Use tonight</div>
              {expiringItems.slice(0, 3).map(item => {
                const d = item.expiry_date ? getDaysUntilExpiry(item) : null;
                return (
                  <div key={item.id} style={{ background: 'rgba(197,81,45,0.08)', border: '1px solid rgba(197,81,45,0.3)', borderRadius: 14, padding: 14, marginBottom: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(197,81,45,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🥫</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#fff' }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--fhv2-terra)' }}>
                        {d === 0 ? 'Today!' : d !== null && d < 0 ? 'Expired' : `Use by ${d === 1 ? 'tomorrow' : `${d} days`}`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* RUNNING LOW */}
          {(() => {
            const low = items.filter(it => (it.quantity || 0) > 0 && (it.quantity || 0) <= 2 && !expiringItems.find(e => e.id === it.id)).slice(0, 5);
            if (low.length === 0) return null;
            return (
              <>
                <div className="fhv2-mlabel">Running low</div>
                {low.map(item => (
                  <div key={item.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--fhv2-line)', borderRadius: 14, padding: 12, marginBottom: 6, display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🥫</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#fff' }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--fhv2-warn)' }}>Low</div>
                    </div>
                    <div style={{ fontFamily: 'var(--fhv2-mono)', fontSize: 10, color: 'var(--fhv2-muted)' }}>{item.quantity} {item.unit || ''}</div>
                  </div>
                ))}
              </>
            );
          })()}

          {/* EMPTY / In shopping list */}
          {(() => {
            const empty = items.filter(it => (it.quantity || 0) === 0).slice(0, 4);
            if (empty.length === 0) return null;
            return (
              <>
                <div className="fhv2-mlabel" style={{ marginTop: 14 }}>Empty · add to list</div>
                {empty.map(item => (
                  <div key={item.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--fhv2-line)', borderRadius: 14, padding: 12, marginBottom: 6, display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(197,81,45,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🥫</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#fff' }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--fhv2-terra)' }}>Empty · in list</div>
                    </div>
                  </div>
                ))}
              </>
            );
          })()}

          {/* Category filter chips — v2 pill style */}
          {items.length > 0 && (
            <>
              <div className="fhv2-mlabel" style={{ marginTop: 16 }}>All items</div>

              {/* Location (zone) chips with live counts */}
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', marginBottom: 8, paddingBottom: 2 }}>
                {([
                  { key: 'all', label: 'All', emoji: '🗂️' },
                  { key: 'fridge', label: 'Fridge', emoji: '🧊' },
                  { key: 'pantry', label: 'Pantry', emoji: '🥫' },
                  { key: 'freezer', label: 'Freezer', emoji: '❄️' },
                  { key: 'other', label: 'Other', emoji: '📦' },
                ] as const).map(zone => {
                  const on = activeLocation === zone.key;
                  const count = zone.key === 'all'
                    ? items.length
                    : items.filter(it => (it.location || 'other') === zone.key).length;
                  if (zone.key !== 'all' && count === 0) return null;
                  return (
                    <button
                      key={zone.key}
                      onClick={() => setActiveLocation(zone.key)}
                      className="fhv2-unset"
                      style={{
                        padding: '6px 12px', borderRadius: 999, flexShrink: 0,
                        border: on ? '1px solid rgba(100, 181, 246, 0.45)' : '1px solid var(--fhv2-line)',
                        background: on ? 'rgba(100, 181, 246, 0.14)' : 'transparent',
                        color: on ? '#fff' : 'var(--fhv2-muted)',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}
                      aria-pressed={on}
                    >
                      <span>{zone.emoji}</span>
                      <span>{zone.label}</span>
                      <span style={{ fontFamily: 'var(--fhv2-mono)', fontSize: 10, opacity: 0.7 }}>{count}</span>
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', marginBottom: 10, paddingBottom: 2 }}>
                {ALL_CATEGORIES.map(cat => {
                  const on = activeCategory === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className="fhv2-unset"
                      style={{
                        padding: '6px 14px', borderRadius: 999, flexShrink: 0,
                        border: on ? '1px solid rgba(197,81,45,0.3)' : '1px solid var(--fhv2-line)',
                        background: on ? 'rgba(197,81,45,0.12)' : 'transparent',
                        color: on ? '#fff' : 'var(--fhv2-muted)',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>

              {(() => {
                const byCategory = activeCategory === 'All' ? items : items.filter(it => it.category === activeCategory);
                const filtered = activeLocation === 'all' ? byCategory : byCategory.filter(it => (it.location || 'other') === activeLocation);
                const nonSurfaced = filtered.filter(it =>
                  !expiringItems.find(e => e.id === it.id) &&
                  !(((it.quantity || 0) > 0 && (it.quantity || 0) <= 2) || (it.quantity || 0) === 0)
                );
                const toShow = nonSurfaced.length > 0 ? nonSurfaced : filtered;
                if (toShow.length === 0) return (
                  <div style={{ fontSize: 12, color: 'var(--fhv2-muted)', padding: 14, textAlign: 'center' }}>
                    No items in {activeCategory}.
                  </div>
                );
                return toShow.slice(0, 30).map(item => (
                  <div key={item.id} onClick={() => openEditModal(item)} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--fhv2-line)', borderRadius: 14, padding: 12, marginBottom: 6, display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🥫</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--fhv2-muted)', marginTop: 2 }}>
                        {item.category}{item.expiry_date ? ` · exp ${new Date(item.expiry_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                      </div>
                    </div>
                    <span className="fhv2-mcard-tail">×{item.quantity}{item.unit ? ` ${item.unit}` : ''}</span>
                  </div>
                ));
              })()}
            </>
          )}

                  </div>

        {/* Add Item Modal */}
        <IonModal isOpen={showAdd} onDidDismiss={() => { setShowAdd(false); setAddMode('suggestions'); clearReceiptItems(); setLocalReceiptItems([]); }} breakpoints={[0, 1]} initialBreakpoint={1}>
          <div className="p-6 space-y-4" style={{ fontFamily: 'Manrope, sans-serif', background: 'var(--fh-dark-surface)', minHeight: '100%' }}>
            <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ background: 'rgba(255,255,255,0.2)' }} />
            <h2 className="text-xl font-extrabold" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', color: 'var(--fh-dark-text)' }}>Add Items</h2>

            {/* Mode toggle */}
            <div className="flex rounded-xl p-1" style={{ background: 'var(--fh-overlay-light)' }}>
              <button
                onClick={() => setAddMode('suggestions')}
                className="flex-1 py-2 rounded-lg text-xs font-bold transition-all"
                style={{
                  background: addMode === 'suggestions' ? 'var(--fh-dark-card)' : 'transparent',
                  color: addMode === 'suggestions' ? '#c5512d' : 'rgba(255,255,255,0.5)',
                }}
              >
                Quick Pick
              </button>
              <button
                onClick={() => setAddMode('manual')}
                className="flex-1 py-2 rounded-lg text-xs font-bold transition-all"
                style={{
                  background: addMode === 'manual' ? 'var(--fh-dark-card)' : 'transparent',
                  color: addMode === 'manual' ? '#c5512d' : 'rgba(255,255,255,0.5)',
                }}
              >
                Type Manually
              </button>
              <button
                onClick={() => { setAddMode('receipt'); clearReceiptItems(); setLocalReceiptItems([]); }}
                className="flex-1 py-2 rounded-lg text-xs font-bold transition-all"
                style={{
                  background: addMode === 'receipt' ? 'var(--fh-dark-card)' : 'transparent',
                  color: addMode === 'receipt' ? '#c5512d' : 'rgba(255,255,255,0.5)',
                }}
              >
                Scan Receipt
              </button>
            </div>

            {/* Hidden file input for receipt upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              onChange={handleReceiptFileSelect}
              style={{ display: 'none' }}
            />

            {addMode === 'suggestions' ? (
              <div className="space-y-3">
                {/* Category tabs */}
                <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                  {Object.keys(SUGGESTIONS).map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSuggestionCategory(cat)}
                      className="whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold flex-shrink-0"
                      style={{
                        background: suggestionCategory === cat ? '#a43917' : 'rgba(255,255,255,0.06)',
                        color: suggestionCategory === cat ? '#fff' : 'rgba(255,255,255,0.5)',
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                {/* Suggestion chips */}
                <div className="flex flex-wrap gap-2" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                  {(SUGGESTIONS[suggestionCategory] || []).map(s => {
                    const alreadyAdded = items.some(it => it.name.toLowerCase() === s.name.toLowerCase());
                    return (
                      <button
                        key={s.name}
                        onClick={() => handleAddSuggestion(s, suggestionCategory)}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl active:scale-95 transition-transform"
                        style={{
                          background: alreadyAdded ? 'rgba(129,199,132,0.1)' : 'rgba(255,255,255,0.06)',
                          border: alreadyAdded ? '1px solid rgba(129,199,132,0.3)' : '1px solid transparent',
                        }}
                      >
                        <Icon name={s.icon} size={16} color={alreadyAdded ? '#81c784' : 'rgba(255,255,255,0.5)'} />
                        <span className="text-xs font-semibold" style={{ color: alreadyAdded ? '#81c784' : '#fff' }}>
                          {s.name}
                        </span>
                        {alreadyAdded && <Icon name="check" size={12} color="#81c784" />}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setShowAdd(false)}
                  className="w-full py-3.5 rounded-xl font-bold text-white text-sm active:scale-95 transition-transform"
                  style={{ background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))' }}
                >
                  Done
                </button>
              </div>
            ) : addMode === 'manual' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--fh-dark-text-secondary)' }}>Item name</label>
                  <input style={inputStyle} value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Oat milk" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--fh-dark-text-secondary)' }}>Category</label>
                  <select style={inputStyle} value={newCategory} onChange={e => setNewCategory(e.target.value)}>
                    {ALL_CATEGORIES.slice(1).map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--fh-dark-text-secondary)' }}>Expiry date (optional)</label>
                  <input style={inputStyle} type="date" value={newExpiry} onChange={e => setNewExpiry(e.target.value)} />
                </div>
                <button
                  onClick={handleAddItem}
                  disabled={!newName.trim()}
                  className="w-full py-3.5 rounded-xl font-bold text-white text-sm disabled:opacity-50 active:scale-95 transition-transform"
                  style={{ background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))', boxShadow: '0 4px 16px rgba(164,57,23,0.25)' }}
                >
                  Add to Pantry
                </button>
              </div>
            ) : (
              /* Receipt scan mode */
              <div className="space-y-4">
                {isScanning ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="animate-spin w-10 h-10 border-3 border-white/20 border-t-[#c5512d] rounded-full mb-4" />
                    <p className="font-semibold text-sm" style={{ color: 'var(--fh-dark-text)' }}>Scanning receipt...</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--fh-dark-text-muted)' }}>This may take a few seconds</p>
                  </div>
                ) : localReceiptItems.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-bold" style={{ color: 'var(--fh-dark-text)' }}>
                        {localReceiptItems.filter(i => i.selected).length} of {localReceiptItems.length} items selected
                      </p>
                      <button
                        onClick={() => {
                          const allSelected = localReceiptItems.every(i => i.selected);
                          setLocalReceiptItems(prev => prev.map(i => ({ ...i, selected: !allSelected })));
                        }}
                        className="text-xs font-bold px-3 py-1 rounded-full"
                        style={{ background: 'var(--fh-overlay-light)', color: 'var(--fh-primary-light)' }}
                      >
                        {localReceiptItems.every(i => i.selected) ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="space-y-1.5" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                      {localReceiptItems.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => toggleReceiptItem(idx)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all active:scale-[0.98]"
                          style={{
                            background: item.selected ? 'rgba(129,199,132,0.08)' : 'rgba(255,255,255,0.04)',
                            border: item.selected ? '1px solid rgba(129,199,132,0.25)' : '1px solid rgba(255,255,255,0.06)',
                          }}
                        >
                          <div
                            className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                            style={{
                              background: item.selected ? '#81c784' : 'rgba(255,255,255,0.1)',
                            }}
                          >
                            {item.selected && <Icon name="check" size={14} color="#0f1923" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold truncate" style={{ color: 'var(--fh-dark-text)' }}>{item.name}</p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs" style={{ color: 'var(--fh-dark-text-muted)' }}>{item.category}</span>
                              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
                              <span className="text-xs" style={{ color: 'var(--fh-dark-text-muted)' }}>Qty: {item.quantity}</span>
                              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>·</span>
                              <span className="text-xs capitalize" style={{ color: 'var(--fh-dark-text-muted)' }}>{item.location}</span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setLocalReceiptItems([]); clearReceiptItems(); }}
                        className="flex-1 py-3 rounded-xl font-bold text-sm active:scale-95 transition-transform"
                        style={{ background: 'var(--fh-overlay-light)', color: 'var(--fh-dark-text-secondary)' }}
                      >
                        Rescan
                      </button>
                      <button
                        onClick={handleAddReceiptItems}
                        disabled={localReceiptItems.filter(i => i.selected).length === 0}
                        className="flex-[2] py-3 rounded-xl font-bold text-white text-sm disabled:opacity-50 active:scale-95 transition-transform"
                        style={{ background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))' }}
                      >
                        Add {localReceiptItems.filter(i => i.selected).length} Items
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                        style={{ background: 'rgba(164,57,23,0.1)' }}
                      >
                        <Icon name="receipt_long" size={32} color="#c5512d" />
                      </div>
                      <p className="font-bold text-sm" style={{ color: 'var(--fh-dark-text)' }}>Scan a Store Receipt</p>
                      <p className="text-xs mt-1 px-4" style={{ color: 'var(--fh-dark-text-muted)' }}>
                        Take a photo or upload an image of your receipt and we'll automatically add the items to your pantry
                      </p>
                      <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
                        Supports JPG, PNG, HEIC, WebP and more
                      </p>
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full py-3.5 rounded-xl font-bold text-white text-sm active:scale-95 transition-transform flex items-center justify-center gap-2"
                      style={{ background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))', boxShadow: '0 4px 16px rgba(164,57,23,0.25)' }}
                    >
                      <Icon name="photo_camera" size={18} color="#ffffff" />
                      Upload Receipt Photo
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </IonModal>

        {/* Edit Item Modal */}
        <IonModal isOpen={!!editItem} onDidDismiss={closeEditModal} breakpoints={[0, 1]} initialBreakpoint={1}>
          <div className="p-6 space-y-4" style={{ fontFamily: 'Manrope, sans-serif', background: 'var(--fh-dark-surface)', minHeight: '100%' }}>
            <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ background: 'rgba(255,255,255,0.2)' }} />
            <h2 className="text-xl font-extrabold" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', color: 'var(--fh-dark-text)' }}>Edit Item</h2>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--fh-dark-text-secondary)' }}>Item name</label>
              <input style={inputStyle} value={editName} onChange={e => setEditName(e.target.value)} placeholder="Item name" />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--fh-dark-text-secondary)' }}>Category</label>
              <select style={inputStyle} value={editCategory} onChange={e => setEditCategory(e.target.value)}>
                {ALL_CATEGORIES.slice(1).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--fh-dark-text-secondary)' }}>Quantity</label>
                <input
                  style={inputStyle}
                  type="number"
                  min="1"
                  value={editQuantity}
                  onChange={e => setEditQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--fh-dark-text-secondary)' }}>Unit</label>
                <input style={inputStyle} value={editUnit} onChange={e => setEditUnit(e.target.value)} placeholder="e.g. pcs, kg, ml" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--fh-dark-text-secondary)' }}>Expiry date</label>
              <input style={inputStyle} type="date" value={editExpiry} onChange={e => setEditExpiry(e.target.value)} />
            </div>

            <button
              onClick={handleSaveEdit}
              disabled={!editName.trim()}
              className="w-full py-3.5 rounded-xl font-bold text-white text-sm disabled:opacity-50 active:scale-95 transition-transform"
              style={{ background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))', boxShadow: '0 4px 16px rgba(164,57,23,0.25)' }}
            >
              Save Changes
            </button>

            <button
              onClick={handleDeleteFromEdit}
              className="w-full py-3 rounded-xl font-bold text-sm active:scale-95 transition-transform"
              style={{ background: 'rgba(239,83,80,0.08)', color: '#ef5350' }}
            >
              Delete Item
            </button>
          </div>
        </IonModal>

        <ConfirmDialog
          open={confirmDelete.open}
          title="Delete Item"
          message="Are you sure you want to delete this pantry item?"
          onConfirm={() => {
            handleDeleteItem(confirmDelete.id);
            setConfirmDelete({ id: '', open: false });
          }}
          onCancel={() => setConfirmDelete({ id: '', open: false })}
        />
      </IonContent>
    </IonPage>
  );
};

export default PantryPage;
